import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Eye,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Package,
  DollarSign,
  Edit,
  Plus,
  Minus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ORDER_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const NASCustomerOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [viewingOrder, setViewingOrder] = useState(null);
  
  // Edit order state
  const [editingOrder, setEditingOrder] = useState(null);
  const [editForm, setEditForm] = useState({
    items: [],
    totalAmount: 0,
    deliveryDate: '',
    deliveryAddress: '',
    notes: '',
    paymentTerms: 'cod'
  });
  const [isSaving, setIsSaving] = useState(false);

  // Users authorized to edit approved orders
  const authorizedEditors = ['arbe', 'admin', 'nicole', 'pabs', 'alma', 'kristine'];
  const canEditApproved = user?.role === 'admin' || 
    authorizedEditors.some(name => 
      user?.username?.toLowerCase().includes(name) || 
      user?.full_name?.toLowerCase().includes(name)
    );

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/api/nas/customer-orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeliverOrder = async (order) => {
    const itemsList = order.items?.map(i => `• ${i.quantity} ${i.unit || 'pcs'} ${i.name}`).join('\n') || '';
    
    if (!window.confirm(
      `Deliver order to ${order.customerName}?\n\nItems:\n${itemsList}\n\nTotal: ₱${order.totalAmount?.toLocaleString()}\n\nThis will DEDUCT stock from Bodega.`
    )) {
      return;
    }
    
    try {
      const response = await axios.put(`${API}/api/nas/customer-orders/${order.id}/deliver`);
      toast.success(`Order delivered! Stock deducted from Bodega.`);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deliver order');
    }
  };

  const handleCancelOrder = async (order) => {
    if (!window.confirm(`Cancel order for ${order.customerName}?`)) return;
    
    try {
      await axios.put(`${API}/api/nas/customer-orders/${order.id}/cancel`);
      toast.success('Order cancelled');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    }
  };

  // Open edit dialog
  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setEditForm({
      items: order.items?.map(item => ({...item})) || [],
      totalAmount: order.totalAmount || 0,
      deliveryDate: order.deliveryDate || '',
      deliveryAddress: order.deliveryAddress || '',
      notes: order.notes || '',
      paymentTerms: order.paymentTerms || 'cod'
    });
  };

  // Update item quantity in edit form
  const updateEditItemQuantity = (index, change) => {
    const newItems = [...editForm.items];
    const newQty = Math.max(1, (newItems[index].quantity || 0) + change);
    newItems[index].quantity = newQty;
    newItems[index].total = newQty * (newItems[index].pricePerUnit || 0);
    
    const newTotal = newItems.reduce((sum, item) => sum + (item.total || 0), 0);
    setEditForm({...editForm, items: newItems, totalAmount: newTotal});
  };

  // Remove item from edit form
  const removeEditItem = (index) => {
    const newItems = editForm.items.filter((_, i) => i !== index);
    const newTotal = newItems.reduce((sum, item) => sum + (item.total || 0), 0);
    setEditForm({...editForm, items: newItems, totalAmount: newTotal});
  };

  // Save edited order
  const handleSaveEdit = async () => {
    if (editForm.items.length === 0) {
      toast.error('Order must have at least one item');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.put(`${API}/api/nas/customer-orders/${editingOrder.id}`, {
        items: editForm.items,
        totalAmount: editForm.totalAmount,
        deliveryDate: editForm.deliveryDate,
        deliveryAddress: editForm.deliveryAddress,
        notes: editForm.notes,
        paymentTerms: editForm.paymentTerms
      });
      toast.success('Order updated successfully!');
      setEditingOrder(null);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = !searchTerm || (order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? true);
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const deliveredToday = orders.filter(o => 
    o.status === 'delivered' && 
    o.deliveredAt?.startsWith(new Date().toISOString().split('T')[0])
  ).length;
  const totalPending = orders
    .filter(o => o.status === 'pending')
    .reduce((sum, o) => sum + (o.totalAmount || 0), 0);

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Customer Orders</h1>
          <p className="text-slate-500">Manage deliveries from Bodega to customers ({orders.length} orders)</p>
        </div>
        <Button variant="outline" onClick={loadOrders} disabled={isLoading}>
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="text-amber-600" size={24} />
              <div>
                <p className="text-sm text-amber-600">Pending Orders</p>
                <p className="text-2xl font-bold text-amber-700">{pendingOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Truck className="text-green-600" size={24} />
              <div>
                <p className="text-sm text-green-600">Delivered Today</p>
                <p className="text-2xl font-bold text-green-700">{deliveredToday}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="text-blue-600" size={24} />
              <div>
                <p className="text-sm text-blue-600">Pending Amount</p>
                <p className="text-2xl font-bold text-blue-700">₱{totalPending.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search by customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(ORDER_STATUS).map(([key, value]) => (
                  <SelectItem key={key} value={key}>{value.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-10 text-center">
              <Package className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500">No customer orders found</p>
            </CardContent>
          </Card>
        ) : (
          filteredOrders.map(order => (
            <Card key={order.id} className={`bg-white ${order.status === 'pending' ? 'border-amber-300' : ''}`}>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800">{order.customerName}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${ORDER_STATUS[order.status]?.color}`}>
                        {ORDER_STATUS[order.status]?.label}
                      </span>
                      {order.stockDeducted && (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full flex items-center gap-1">
                          <Package size={12} />
                          Stock Deducted
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      <span>{order.items?.length || 0} items</span>
                      <span className="font-medium text-green-600">₱{order.totalAmount?.toLocaleString()}</span>
                      <span>Created: {new Date(order.createdAt).toLocaleDateString()}</span>
                      {order.deliveryDate && <span>Delivery: {order.deliveryDate}</span>}
                    </div>
                    {/* Items Preview */}
                    <div className="mt-2 text-xs text-slate-400">
                      {order.items?.slice(0, 3).map((item, idx) => (
                        <span key={idx}>
                          {item.quantity} {item.name}
                          {idx < Math.min(order.items.length, 3) - 1 ? ', ' : ''}
                        </span>
                      ))}
                      {order.items?.length > 3 && ` +${order.items.length - 3} more`}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => setViewingOrder(order)}>
                      <Eye size={16} className="mr-1" /> View
                    </Button>
                    {order.status !== 'delivered' && order.status !== 'cancelled' && (
                      <>
                        {/* Edit button - show for pending orders or approved orders if user is authorized */}
                        {(order.status === 'pending' || (order.status === 'approved' && canEditApproved)) && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEditOrder(order)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit size={16} className="mr-1" /> Edit
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleDeliverOrder(order)}
                        >
                          <Truck size={16} className="mr-1" /> Deliver
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleCancelOrder(order)}
                        >
                          <XCircle size={16} className="mr-1" /> Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* View Order Modal */}
      {viewingOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Order Details</h2>
              <Button variant="ghost" size="sm" onClick={() => setViewingOrder(null)}>×</Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-lg">{viewingOrder.customerName}</p>
                  <p className="text-sm text-slate-500">{viewingOrder.deliveryAddress}</p>
                </div>
                <span className={`px-3 py-1 rounded-full ${ORDER_STATUS[viewingOrder.status]?.color}`}>
                  {ORDER_STATUS[viewingOrder.status]?.label}
                </span>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-2">Item</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Price</th>
                      <th className="text-right p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingOrder.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">{item.name}</td>
                        <td className="text-right p-2">{item.quantity}</td>
                        <td className="text-right p-2">₱{item.pricePerUnit?.toLocaleString()}</td>
                        <td className="text-right p-2">₱{item.total?.toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr className="border-t bg-green-50 font-semibold">
                      <td colSpan={3} className="p-2">Total</td>
                      <td className="text-right p-2 text-green-600">₱{viewingOrder.totalAmount?.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Payment Terms</p>
                  <p className="font-medium">{viewingOrder.paymentTerms?.toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-slate-500">Created By</p>
                  <p className="font-medium">{viewingOrder.createdBy}</p>
                </div>
                {viewingOrder.deliveredAt && (
                  <>
                    <div>
                      <p className="text-slate-500">Delivered At</p>
                      <p className="font-medium">{new Date(viewingOrder.deliveredAt).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Delivered By</p>
                      <p className="font-medium">{viewingOrder.deliveredBy}</p>
                    </div>
                  </>
                )}
              </div>

              {viewingOrder.notes && (
                <div>
                  <p className="text-slate-500 text-sm">Notes</p>
                  <p className="text-sm">{viewingOrder.notes}</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setViewingOrder(null)}>Close</Button>
              {viewingOrder.status !== 'delivered' && viewingOrder.status !== 'cancelled' && (
                <>
                  {(viewingOrder.status === 'pending' || (viewingOrder.status === 'approved' && canEditApproved)) && (
                    <Button 
                      variant="outline" 
                      onClick={() => { setViewingOrder(null); handleEditOrder(viewingOrder); }}
                      className="text-blue-600"
                    >
                      <Edit size={16} className="mr-1" /> Edit Order
                    </Button>
                  )}
                  <Button className="bg-green-600" onClick={() => { setViewingOrder(null); handleDeliverOrder(viewingOrder); }}>
                    <Truck size={16} className="mr-1" /> Deliver Order
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Dialog */}
      <Dialog open={!!editingOrder} onOpenChange={(open) => !open && setEditingOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={20} className="text-blue-600" />
              Edit Order - {editingOrder?.customerName}
            </DialogTitle>
          </DialogHeader>
          
          {editingOrder && (
            <div className="space-y-4">
              {/* Order Status Badge */}
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm ${ORDER_STATUS[editingOrder.status]?.color}`}>
                  {ORDER_STATUS[editingOrder.status]?.label}
                </span>
                {editingOrder.status === 'approved' && (
                  <span className="text-sm text-amber-600">(Editing approved order)</span>
                )}
              </div>
              
              {/* Items */}
              <div className="space-y-2">
                <Label>Order Items</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {editForm.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-slate-500">
                          ₱{item.pricePerUnit?.toLocaleString()} × {item.quantity} = ₱{item.total?.toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateEditItemQuantity(index, -1)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus size={14} />
                        </Button>
                        <span className="w-12 text-center font-semibold">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateEditItemQuantity(index, 1)}
                        >
                          <Plus size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700"
                          onClick={() => removeEditItem(index)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {editForm.items.length === 0 && (
                  <p className="text-center text-slate-400 py-4">No items in order</p>
                )}
              </div>
              
              {/* Total */}
              <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                <span className="font-semibold">Total Amount</span>
                <span className="text-xl font-bold text-green-600">₱{editForm.totalAmount?.toLocaleString()}</span>
              </div>
              
              {/* Delivery Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Delivery Date</Label>
                  <Input
                    type="date"
                    value={editForm.deliveryDate}
                    onChange={(e) => setEditForm({...editForm, deliveryDate: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <Select 
                    value={editForm.paymentTerms} 
                    onValueChange={(v) => setEditForm({...editForm, paymentTerms: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cod">Cash on Delivery</SelectItem>
                      <SelectItem value="credit">Credit</SelectItem>
                      <SelectItem value="partial">Partial Payment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Delivery Address</Label>
                <Input
                  value={editForm.deliveryAddress}
                  onChange={(e) => setEditForm({...editForm, deliveryAddress: e.target.value})}
                  placeholder="Delivery address"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Order notes..."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingOrder(null)}>Cancel</Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={isSaving || editForm.items.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASCustomerOrders;
